import { useCallback, useEffect, useMemo, type FC } from 'react';
import { useRect } from '@darkroom.engineering/hamo';
import { getContrastRatio } from '@repo/shared/helpers';
import { useInView } from 'framer-motion';

import { useNavTheme, type NavDarkSurface } from '#/lib/nav-theme';
import type { SanityPageSection } from '#/lib/sanity/queries/sections';
import { createSanityDataAttribute } from '#/lib/sanity/visualEditing';
import { getNavDarkSurface, isDarkModuleTheme } from '#/lib/section-theme';
import type { PageLayoutSharedProps } from '#/lib/types';
import { useAppSizes } from '#/lib/use-app-sizes';
import { camelCaseToTitleCase, cn, COLORS } from '#/lib/utils';
import { AnchorPoint } from '#/components/AnchorPoint';
import { AwardsCta } from '#/components/AwardsCta';
import { BentoBox } from '#/components/BentoBox';
import { CallToAction } from '#/components/CallToAction';
import { CardGrid } from '#/components/CardGrid';
import { CareersFeed } from '#/components/CareersFeed';
import { ComparisonCards } from '#/components/ComparisonCards';
import { ComparisonTable } from '#/components/ComparisonTable';
import { Divider } from '#/components/Divider';
import { DocumentTemplate } from '#/components/DocumentTemplate';
import { EditorialCollage } from '#/components/EditorialCollage';
import { Faq } from '#/components/Faq';
import { FeatureComparison } from '#/components/FeatureComparison';
import { Footnotes } from '#/components/Footnotes';
import { FullWidthMedia } from '#/components/FullWidthMedia';
import { HeroBasic } from '#/components/HeroBasic';
import { HeroCentered } from '#/components/HeroCentered';
import { HeroHome } from '#/components/HeroHome';
import { HeroNewsletter } from '#/components/HeroNewsletter';
import { HeroProduct } from '#/components/HeroProduct';
import { HeroSplit } from '#/components/HeroSplit';
import { HeroTwoUp } from '#/components/HeroTwoUp';
import { HomepageContentCards } from '#/components/HomepageContentCards';
import { HomepageEvents } from '#/components/HomepageEvents';
import { ImageGrid } from '#/components/ImageGrid';
import { LinkCardGrid } from '#/components/LinkCardGrid';
import { LogoGrid } from '#/components/LogoGrid';
import { LogoMarquee } from '#/components/LogoMarquee';
import { NewsletterListing } from '#/components/NewsletterListing';
import { PageSections } from '#/components/PageSections';
import { PartnerGrid } from '#/components/PartnerGrid';
import { PhotoMarquee } from '#/components/PhotoMarquee';
import { PolicyEngineAnimation } from '#/components/PolicyEngineAnimation';
import { PressListing } from '#/components/PressListing';
import { RailsFormLanding } from '#/components/RailsFormLanding';
import { RelatedContent } from '#/components/RelatedContent';
import { RichAccordion } from '#/components/RichAccordion';
import { RichTextSection } from '#/components/RichTextSection';
import { SolutionsTabs } from '#/components/SolutionsTabs';
import { SombraStack } from '#/components/SombraStack';
import { Stats } from '#/components/Stats';
import { TableSection } from '#/components/TableSection';
import { TestimonialCarousel } from '#/components/TestimonialCarousel';
import { TestimonialGrid } from '#/components/TestimonialGrid';
import { TestimonialMarquee } from '#/components/TestimonialMarquee';
import { TestimonialSection } from '#/components/TestimonialSection';
import { ThreeUp } from '#/components/ThreeUp';
import { Timeline } from '#/components/Timeline';
import { TwoUp } from '#/components/TwoUp';
import { TwoUpCard } from '#/components/TwoUpCard';
import { TwoUpForm } from '#/components/TwoUpForm';
import { TwoUpScroller } from '#/components/TwoUpScroller';
import { VideoTwoUp } from '#/components/VideoTwoUp';

import { EventHero } from './EventHero';
import { HeroForm } from './HeroForm';
import { HeroHomepage } from './HomePageHero';
import { HomePagePainpoint } from './HomePagePain/HomePagePain';
import { HomePageTwoUp } from './HomePageTwoUp/HomePageTwoUp';
import { RailsArchitecture } from './RailsArchitecture/RailsArchitecture';
import { RailsSombraCombo } from './RailsSombraCombo/RailsSombraCombo';

type Props = PageLayoutSharedProps & {
  section: SanityPageSection;
  index: number;
};

const COMPONENT_MAP: {
  [K in SanityPageSection['_type']]: FC<
    Extract<SanityPageSection, { _type: K }>
  >;
} = {
  sectionReference: PageSections,
  awardsCta: AwardsCta,
  logoMarquee: LogoMarquee,
  testimonialSection: TestimonialSection,
  twoUp: TwoUp,
  videoTwoUp: VideoTwoUp,
  fullWidthVideo: FullWidthMedia,
  twoUpScroller: TwoUpScroller,
  testimonialMarquee: TestimonialMarquee,
  callToAction: CallToAction,
  heroBasic: HeroBasic,
  heroCentered: HeroCentered,
  heroHome: HeroHome,
  heroProduct: HeroProduct,
  heroSplit: HeroSplit,
  heroTwoUp: HeroTwoUp,
  heroNewsletter: HeroNewsletter,
  threeUp: ThreeUp,
  stats: Stats,
  linkCardGrid: LinkCardGrid,
  faqSection: Faq,
  twoUpCard: TwoUpCard,
  photoMarquee: PhotoMarquee,
  editorialCollage: EditorialCollage,
  partnerGrid: PartnerGrid,
  newsletterListing: NewsletterListing,
  bentoBox: BentoBox,
  careersFeed: CareersFeed,
  testimonialCarousel: TestimonialCarousel,
  testimonialGrid: TestimonialGrid,
  logoGrid: LogoGrid,
  pressListing: PressListing,
  richAccordion: RichAccordion,
  richTextSection: RichTextSection,
  relatedContent: RelatedContent,
  homepageContentCards: HomepageContentCards,
  homepageEvents: HomepageEvents,
  twoUpForm: TwoUpForm,
  documentTemplate: DocumentTemplate,
  divider: Divider,
  anchorPoint: AnchorPoint,
  solutionsTabs: SolutionsTabs,
  timeline: Timeline,
  comparisonTable: ComparisonTable,
  featureComparison: FeatureComparison,
  comparisonCards: ComparisonCards,
  heroForm: HeroForm,
  eventHero: EventHero,
  homePageHero: HeroHomepage,
  homePageTwoUp: HomePageTwoUp,
  homePagePainpoint: HomePagePainpoint,
  railsArchitecture: RailsArchitecture,
  railsSombraCombo: RailsSombraCombo,
  cardGrid: CardGrid,
  imageGrid: ImageGrid,
  sombraStack: SombraStack,
  policyEngineAnimation: PolicyEngineAnimation,
  tableSection: TableSection,
  railsFormLanding: RailsFormLanding,
  footnotes: Footnotes,
};

function sectionHasBackground(
  section: SanityPageSection,
): section is Extract<SanityPageSection, { background?: string }> {
  return 'background' in section && typeof section.background === 'string';
}

export function PageSection({
  section,
  index,
  initialData,
  isPreview,
  sanitySource,
}: Props) {
  const Component = COMPONENT_MAP[section._type] as
    | FC<SanityPageSection>
    | undefined;

  /**
   * Logic for vertical spacing between sections
   * ===========================================
   *
   * 1. If the section has a background color, remove the top and bottom margin so
   * sections can stack on top of each other and share a common background color
   * 2. If the section is in the `sectionsWithoutMargin` set, remove the top and bottom margin always
   * 3. If the section is in the `sectionsWithoutMarginTop` set and it has a background, remove the top margin only.
   * 4. If it's the first section, it has no top margin
   * */

  // Always-dark modules (no light theme option). Themed modules like
  // homepageContentCards invert the nav via hasDarkTheme instead, so a light
  // Content Cards band matches the light CTA below it.
  const forcedDarkSections = [
    'heroHome',
    'homePageHero',
    'homePageTwoUp',
    'homePagePainpoint',
    'railsArchitecture',
    'railsSombraCombo',
    'awardsCta',
    'sombraStack',
    'policyEngineAnimation',
  ];
  const sectionHasForcedBackground = forcedDarkSections.includes(section._type);
  const hasBackground = sectionHasBackground(section);
  const sectionsWithoutMargin = new Set([
    'heroProduct',
    'testimonialMarquee',
    'testimonialCarousel',
    'testimonialSection',
    'sectionReference',
    'anchorPoint',
    'cardGrid',
    'twoUp',
    'videoTwoUp',
    'fullWidthVideo',
    'twoUpForm',
    'callToAction',
    'heroCentered',
    'heroSplit',
    'heroForm',
    'eventHero',
    'stats',
    'divider',
    'tableSection',
    'featureComparison',
    'comparisonTable',
    'homepageContentCards',
    'homepageEvents',
    'railsFormLanding',
    'imageGrid',
    'footnotes',
    ...forcedDarkSections,
  ]);
  const sectionsWithoutMarginTop = new Set(['threeUp']);
  const isFirstSection = index === 0;

  let hasMarginTop = true;
  let hasMarginBottom = true;

  if (
    sectionsWithoutMargin.has(section._type) ||
    hasBackground ||
    sectionHasForcedBackground
  ) {
    hasMarginTop = false;
    hasMarginBottom = false;
  }

  if (sectionsWithoutMarginTop.has(section._type) && hasBackground) {
    hasMarginTop = false;
    hasMarginBottom = true;
  }

  if (isFirstSection) {
    hasMarginTop = false;
  }

  /**
   * Logic for inverting the navigation color desktop only when the section has a background color
   */
  // Dark-themed modules carry their surface colour via the `theme` token (not
  // a `background` hex), so opt them into nav inversion. isDarkModuleTheme
  // also handles modules with their own theme vocabulary (e.g. the stats
  // "electric" theme paints a dark charcoal band).
  const hasDarkTheme =
    'theme' in section && isDarkModuleTheme(section._type, section.theme);
  const shouldInvertNav =
    sectionHasForcedBackground ||
    hasDarkTheme ||
    (hasBackground &&
      getContrastRatio(
        section.background ?? COLORS.cloud,
        COLORS.grayscale['06'],
      ) < 4.5);
  // Purple → white logo; charcoal / forced / hex dark bands → Cipher 5 logo.
  const navDarkSurface: NavDarkSurface =
    ('theme' in section && getNavDarkSurface(section._type, section.theme)) ||
    'charcoal';
  const { registerSection, unregisterSection } = useNavTheme();

  const { headerHeight } = useAppSizes();

  // Create a mutable ref object that both hooks can use
  const containerRef = useMemo(
    () => ({ current: null as HTMLDivElement | null }),
    [],
  );

  const [setRef, rect] = useRect({
    debounce: 100,
    ignoreTransform: true,
    callback: null,
  });

  const sectionId = `${section._key}-${index}`;

  const isInView = useInView(containerRef, {
    once: false,
  });

  const refCallback = useCallback(
    (el: HTMLDivElement | null) => {
      containerRef.current = el;
      setRef(el);
    },
    [setRef, containerRef],
  );

  useEffect(() => {
    const top = isFirstSection ? 0 : rect.top - headerHeight / 2;
    const bottom = rect.bottom - headerHeight / 2;
    if (isNaN(top) || isNaN(bottom)) return;

    if (shouldInvertNav) {
      registerSection(sectionId, top, bottom, navDarkSurface);
    }
    // eslint-disable-next-line
  }, [rect, isFirstSection, shouldInvertNav, headerHeight]);

  useEffect(() => {
    return () => {
      unregisterSection(sectionId);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isInView) {
      containerRef.current?.classList.remove('is-hidden');
    }
  }, [isInView, containerRef]);

  if (!Component) {
    return (
      <div key={section._key} className="px-25 py-100 text-center">
        No component found for {section._type}
      </div>
    );
  }

  const sectionSource = sanitySource
    ? {
        ...sanitySource,
        path: `${sanitySource.path}[_key=="${section._key}"]`,
      }
    : undefined;
  const dataSanity = sectionSource
    ? createSanityDataAttribute(sectionSource)
    : undefined;

  // Pass initialData prop only to CareersFeed component
  const sectionReferenceSource =
    section._type === 'sectionReference' && section.referencedSectionId
      ? {
          documentId: section.referencedSectionId,
          documentType: 'section',
          path: 'sections',
        }
      : undefined;
  const baseComponentProps = {
    ...section,
    sectionIndex: index,
    sanitySource: sectionReferenceSource ?? sectionSource,
  };
  const componentProps =
    section._type === 'careersFeed' && initialData
      ? {
          ...baseComponentProps,
          initialJobs: initialData.jobs,
        }
      : baseComponentProps;
  return (
    <section
      ref={refCallback}
      key={section._key}
      data-sanity={dataSanity}
      data-section={section._type}
      className={cn('is-hidden relative', {
        'mt-60 m:mt-120': hasMarginTop,
        'mb-60 m:mb-120': hasMarginBottom,
      })}
    >
      {isPreview && (
        <div className="absolute top-40 right-0  bg-black border-cloud border-2 px-12 py-5 text-14 font-normal leading-150 text-cloud z-20 rounded-l-10 ">
          {camelCaseToTitleCase(section._type)}
        </div>
      )}
      <Component {...componentProps} />
    </section>
  );
}
